import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserGroupIcon,
  ArrowLeftIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';

const EventDetails = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: '',
    email: '',
    company: '',
    dietaryRestrictions: '',
    questions: '',
  });
  const [registering, setRegistering] = useState(false);
  const [unregistering, setUnregistering] = useState(false);
  const { user: currentUser } = useAuthStore();
  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchEvent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api.events.getById(id);
        setEvent(data);
        setIsRegistered(data.is_registered || false); // backend should return this if possible
      } catch (err) {
        setError('Failed to load event.');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await api.events.register(id);
      setIsRegistered(true);
      // Optionally refresh event data
      const data = await api.events.getById(id);
      setEvent(data);
    } catch (err) {
      alert('Failed to register for event.');
    } finally {
      setRegistering(false);
    }
  };

  const handleUnregister = async () => {
    setUnregistering(true);
    try {
      await api.events.unregister(id);
      setIsRegistered(false);
      // Optionally refresh event data
      const data = await api.events.getById(id);
      setEvent(data);
    } catch (err) {
      alert('Failed to unregister from event.');
    } finally {
      setUnregistering(false);
    }
  };

  const handleRegistration = (e) => {
    e.preventDefault();
    // TODO: Implement registration logic
    console.log('Event registration:', {
      eventId: id,
      ...registrationData,
      timestamp: new Date().toISOString(),
    });
    setIsRegistered(true);
    setShowRegistrationModal(false);
    setRegistrationData({
      name: '',
      email: '',
      company: '',
      dietaryRestrictions: '',
      questions: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Are you sure you want to delete this event? This action cannot be undone.')) return;
    setDeleteLoading(true);
    try {
      await api.events.delete(event.id);
      navigate('/community');
    } catch (err) {
      alert('Failed to delete event.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditEvent = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      await api.events.update(event.id, editEvent);
      setShowEditModal(false);
      // Refresh event data
      const data = await api.events.getById(event.id);
      setEvent(data);
    } catch (err) {
      alert('Failed to update event.');
    } finally {
      setEditLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Event not found.'}</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Events
          </button>
        </div>
      </div>
    );
  }

  const isEventFull = event.participants_count >= event.max_participants;
  const registrationProgress = event.max_participants > 0 ? (event.participants_count / event.max_participants) * 100 : 0;

  return (
    <div className="min-h-screen bg-transparent relative">
      {/* Noise Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.015] mix-blend-soft-light pointer-events-none">
        <svg className="w-full h-full">
          <filter id="noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#noise)" />
        </svg>
      </div>

      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/40 via-purple-100/40 to-pink-100/40 dark:from-indigo-900/20 dark:via-purple-900/20 dark:to-pink-900/20" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-indigo-200/30 via-transparent to-transparent dark:from-indigo-800/20" />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => window.history.back()}
          className="inline-flex items-center text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 mb-8"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Events
        </button>

        {/* Event Header */}
        <div className="bg-white/30 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-8 mb-8 border border-gray-200/20 dark:border-gray-700/20">
          {/* Event image or SVG placeholder */}
          {event.image_url ? (
            <img src={event.image_url} alt={event.title} className="w-full h-56 object-cover rounded-lg mb-6" />
          ) : (
            (() => {
              // Deterministic gradient selection based on event id or title
              const gradients = [
                ['from-pink-200', 'to-yellow-200', 'dark:from-pink-900', 'dark:to-yellow-900'],
                ['from-indigo-100', 'to-purple-100', 'dark:from-indigo-900', 'dark:to-purple-900'],
                ['from-green-100', 'to-blue-100', 'dark:from-green-900', 'dark:to-blue-900'],
                ['from-yellow-100', 'to-pink-100', 'dark:from-yellow-900', 'dark:to-pink-900'],
                ['from-blue-100', 'to-cyan-100', 'dark:from-blue-900', 'dark:to-cyan-900'],
                ['from-purple-100', 'to-fuchsia-100', 'dark:from-purple-900', 'dark:to-fuchsia-900'],
              ];
              const hash = (event.id || event.title || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
              const grad = gradients[hash % gradients.length];
              return (
                <div className={`w-full h-56 flex items-center justify-center rounded-lg mb-6 bg-gradient-to-br ${grad[0]} ${grad[1]} ${grad[2]} ${grad[3]}`}> 
                  <svg
                    width="100"
                    height="100"
                    viewBox="0 0 48 48"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="opacity-90"
                    aria-label="Event placeholder"
                  >
                    {/* Background circle */}
                    <circle cx="24" cy="24" r="22" fill="#c7d2fe" opacity="0.3" />
                    {/* Main icon: calendar */}
                    <rect x="12" y="16" width="24" height="18" rx="4" fill="#6366f1" />
                    <rect x="12" y="16" width="24" height="4" rx="2" fill="#818cf8" />
                    <rect x="18" y="24" width="12" height="2.5" rx="1.25" fill="#fff" opacity="0.7" />
                    <rect x="18" y="28" width="8" height="2" rx="1" fill="#fff" opacity="0.5" />
                    {/* Decorative dots */}
                    <circle cx="8" cy="10" r="1" fill="#fbbf24" />
                    <circle cx="40" cy="12" r="1.2" fill="#f472b6" />
                    <circle cx="24" cy="44" r="0.8" fill="#34d399" />
                  </svg>
                </div>
              );
            })()
          )}
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">{event.title}</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div className="flex items-center text-gray-600 dark:text-gray-300">
                <CalendarIcon className="h-5 w-5 mr-3" />
                <span>{new Date(event.start_date).toLocaleString()} - {new Date(event.end_date).toLocaleString()}</span>
              </div>
              <div className="flex items-center text-gray-600 dark:text-gray-300">
                <ClockIcon className="h-5 w-5 mr-3" />
                <span>Duration: {Math.round((new Date(event.end_date) - new Date(event.start_date)) / (1000 * 60 * 60))} hours</span>
              </div>
              <div className="flex items-center text-gray-600 dark:text-gray-300">
                {event.location_type === 'online' ? (
                  <GlobeAltIcon className="h-5 w-5 mr-3" />
                ) : (
                  <BuildingOfficeIcon className="h-5 w-5 mr-3" />
                )}
                <div>
                  <span>{event.location}</span>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center text-gray-600 dark:text-gray-300">
                <UserGroupIcon className="h-5 w-5 mr-3" />
                <span>{event.participants_count}/{event.max_participants} participants</span>
              </div>
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                  Organizer: <span className="font-medium text-gray-900 dark:text-white">{event.organizer_name}</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Organizer ID: {event.organizer_id}
                </div>
              </div>
            </div>
          </div>
          {/* Registration Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
              <span>Registration Progress</span>
              <span>{Math.round(registrationProgress)}% full</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className="bg-indigo-600 h-3 rounded-full"
                style={{ width: `${registrationProgress}%` }}
              />
            </div>
          </div>
          {/* Registration Status & Actions */}
          {isRegistered ? (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" />
                <span className="text-green-800 dark:text-green-200 font-medium">
                  You're registered!
                </span>
              </div>
              <button
                onClick={handleUnregister}
                disabled={unregistering}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {unregistering ? 'Unregistering...' : 'Unregister'}
              </button>
            </div>
          ) : isEventFull ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <ExclamationCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400 mr-2" />
                <span className="text-red-800 dark:text-red-200 font-medium">
                  Event is full
                </span>
              </div>
              <p className="text-red-700 dark:text-red-300 text-sm mt-1">
                Registration is closed.
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                <span className="text-blue-800 dark:text-blue-200 font-medium">
                  Registration open
                </span>
              </div>
              <button
                onClick={handleRegister}
                disabled={registering}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {registering ? 'Registering...' : 'Register'}
              </button>
            </div>
          )}
          {/* Organizer controls */}
          {currentUser && event.organizer_id === currentUser.id && (
            <div className="flex space-x-2 mb-4">
              <button
                onClick={() => {
                  setEditEvent({
                    title: event.title,
                    description: event.description,
                    category: event.category,
                    location_type: event.location_type,
                    location: event.location,
                    start_date: event.start_date,
                    end_date: event.end_date,
                    max_participants: event.max_participants,
                    image_url: event.image_url,
                    tags: event.tags,
                  });
                  setShowEditModal(true);
                }}
                className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
              >
                Edit Event
              </button>
              <button
                onClick={handleDeleteEvent}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Event'}
              </button>
            </div>
          )}
          <div className="mt-6 text-gray-700 dark:text-gray-300">
            <h2 className="text-xl font-semibold mb-2">Description</h2>
            <p>{event.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {(Array.isArray(event.tags) ? event.tags : []).map(tag => (
                <span key={tag} className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-sm">{tag}</span>
              ))}
            </div>
          </div>
          <div className="mt-6 text-gray-700 dark:text-gray-300 text-sm">
            <div><span className="font-medium">Event ID:</span> {event.id}</div>
            <div><span className="font-medium">Created:</span> {new Date(event.created_at).toLocaleString()}</div>
            <div><span className="font-medium">Updated:</span> {new Date(event.updated_at).toLocaleString()}</div>
        </div>
        </div>
      </div>

      {/* Registration Modal */}
      {showRegistrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Register for {event.title}
              </h2>
              <button
                onClick={() => setShowRegistrationModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleRegistration} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={registrationData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={registrationData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Company/Organization
                </label>
                <input
                  type="text"
                  name="company"
                  value={registrationData.company}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Dietary Restrictions
                </label>
                <input
                  type="text"
                  name="dietaryRestrictions"
                  value={registrationData.dietaryRestrictions}
                  onChange={handleInputChange}
                  placeholder="Any allergies or dietary needs"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Questions or Comments
                </label>
                <textarea
                  name="questions"
                  value={registrationData.questions}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Any questions for the organizers?"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRegistrationModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Register
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Event</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleEditEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={editEvent.title}
                  onChange={e => setEditEvent(prev => ({ ...prev, title: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={editEvent.description}
                  onChange={e => setEditEvent(prev => ({ ...prev, description: e.target.value }))}
                  required
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                <input
                  type="text"
                  value={editEvent.category}
                  onChange={e => setEditEvent(prev => ({ ...prev, category: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location Type</label>
                <select
                  value={editEvent.location_type}
                  onChange={e => setEditEvent(prev => ({ ...prev, location_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="online">Online</option>
                  <option value="onsite">On-site</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Location</label>
                <input
                  type="text"
                  value={editEvent.location}
                  onChange={e => setEditEvent(prev => ({ ...prev, location: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                  <input
                    type="datetime-local"
                    value={editEvent.start_date ? new Date(editEvent.start_date).toISOString().slice(0, 16) : ''}
                    onChange={e => setEditEvent(prev => ({ ...prev, start_date: new Date(e.target.value).toISOString() }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                  <input
                    type="datetime-local"
                    value={editEvent.end_date ? new Date(editEvent.end_date).toISOString().slice(0, 16) : ''}
                    onChange={e => setEditEvent(prev => ({ ...prev, end_date: new Date(e.target.value).toISOString() }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Participants</label>
                <input
                  type="number"
                  value={editEvent.max_participants}
                  onChange={e => setEditEvent(prev => ({ ...prev, max_participants: parseInt(e.target.value) }))}
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
                <input
                  type="text"
                  value={editEvent.image_url}
                  onChange={e => setEditEvent(prev => ({ ...prev, image_url: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags (comma separated)</label>
                <input
                  type="text"
                  value={Array.isArray(editEvent.tags) ? editEvent.tags.join(', ') : ''}
                  onChange={e => setEditEvent(prev => ({ ...prev, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;